import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, Check, X, UserPlus } from "lucide-react";

function label(c) {
  if (!c) return "";
  const bits = [c.name];
  const extra = [c.city, c.gstin].filter(Boolean).join(" · ");
  return extra ? `${bits[0]} — ${extra}` : bits[0] || "";
}

/**
 * Searchable client picker.
 *
 * A plain <select> stops being usable past a handful of clients and gives no
 * hint about which "Acme" is meant when several cities share a name. This lists
 * matches with their GSTIN/city and keeps the full account-wide client list
 * reachable without paging through it.
 */
export default function ClientPicker({
  clients = [],
  value = "",
  onChange,
  onCreateNew,
  placeholder = "Search clients by name, email, phone or GSTIN…",
  allowNone = true,
  noneLabel = "— None (enter manually) —",
  testid = "client-picker",
  maxVisible = 60,
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);

  const selected = useMemo(
    () => clients.find((c) => c.id === value) || null,
    [clients, value],
  );

  // Search across the fields a user would actually remember.
  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const pool = needle
      ? clients.filter((c) =>
          [c.name, c.email, c.phone, c.gstin, c.city]
            .filter(Boolean)
            .some((f) => String(f).toLowerCase().includes(needle)),
        )
      : clients;
    return pool.slice(0, maxVisible);
  }, [clients, query, maxVisible]);

  useEffect(() => { setActive(0); }, [query]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const choose = (client) => {
    if (onChange) onChange(client ? client.id : "");
    setQuery("");
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((i) => Math.min(i + 1, matches.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (open && matches[active]) choose(matches[active]);
      else if (allowNone && query.trim() === "") choose(null);
    } else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative" ref={boxRef} data-testid={testid}>
      <div className="relative">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          value={open ? query : (selected ? label(selected) : "")}
          onFocus={() => { setOpen(true); setQuery(""); }}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={onKeyDown}
          placeholder={selected ? label(selected) : placeholder}
          className="w-full h-10 pl-9 pr-8 border border-[var(--border)] rounded-md bg-white text-sm"
          data-testid={`${testid}-input`}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
        />
        {(selected || query) && (
          <button
            type="button"
            onClick={() => choose(null)}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
            title="Clear selection"
            data-testid={`${testid}-clear`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-64 overflow-auto card-flat shadow-lg" data-testid={`${testid}-menu`}>
          {allowNone && query.trim() === "" && (
            <button
              type="button"
              onClick={() => choose(null)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
              data-testid={`${testid}-none`}
            >
              {noneLabel}
            </button>
          )}

          {matches.length === 0 && (
            <div className="px-3 py-3 text-sm text-gray-500">
              No client matches “{query}”.
              {onCreateNew && (
                <button
                  type="button"
                  onClick={() => { onCreateNew(query.trim()); setOpen(false); }}
                  className="mt-2 flex items-center gap-1.5 text-sm underline"
                  data-testid={`${testid}-create`}
                >
                  <UserPlus className="w-3.5 h-3.5" /> Create “{query.trim()}”
                </button>
              )}
            </div>
          )}

          {matches.map((c, i) => (
            <button
              key={c.id}
              type="button"
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(c)}
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 ${i === active ? "bg-gray-100" : "hover:bg-gray-50"}`}
              data-testid={`${testid}-opt-${c.id}`}
            >
              <span className="truncate">
                <span className="font-medium">{c.name || "Unnamed client"}</span>
                <span className="text-gray-500 text-xs ml-2">
                  {[c.city, c.gstin].filter(Boolean).join(" · ")}
                </span>
              </span>
              {c.id === value && <Check className="w-4 h-4 text-emerald-600 shrink-0" />}
            </button>
          ))}

          {matches.length > 0 && onCreateNew && (
            <button
              type="button"
              onClick={() => { onCreateNew(query.trim()); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm border-t border-[var(--border)] text-gray-600 hover:bg-gray-50 flex items-center gap-1.5"
              data-testid={`${testid}-create-bottom`}
            >
              <UserPlus className="w-3.5 h-3.5" />
              {query.trim() ? `Create “${query.trim()}”` : "New client"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}